// 时区友好名称映射
// 后端字典 gp_time_zone 返回的 label/value 可能是 GMT/UTC 偏移量（如 "GMT-5"、"-5"）
// 也可能带有类 IANA 时区片段（如 "America/New_York"）。这里统一转换成美国用户
// 熟悉的通俗名称（如 "Eastern Time (EST)"），匹配不到时原样返回，避免展示空白。

// ---- 偏移量 → 通俗名称（未列出的偏移量回退为标准化 "GMT±n"） ----
// 注意：后端字典为「固定偏移」而非真实时区（无夏令时规则）。美国运营者在夏令时期间
// 需选择 -4 才能显示正确的东部时间，故负偏移用「标准/夏令」双名标注，避免误导。
const OFFSET_NAMES = {
  "0": "Greenwich Mean Time (GMT)",
  "1": "Central European Time (CET)",
  "2": "Eastern European Time (EET)",
  "3": "Arabia Standard Time (GMT+3)",
  "4": "Gulf Standard Time (GMT+4)",
  "5": "Pakistan Standard Time (GMT+5)",
  "7": "Indochina Time (GMT+7)",
  "8": "China Standard Time (GMT+8)",
  "9": "Japan Standard Time (JST)",
  "10": "Australian Eastern Time (AEST)",
  "12": "New Zealand Time (GMT+12)",
  "-3": "Argentina/Brasília (GMT-3)",
  "-4": "Eastern Daylight / Atlantic (GMT-4)",
  "-5": "Eastern Standard / Central Daylight (GMT-5)",
  "-6": "Central Standard / Mountain Daylight (GMT-6)",
  "-7": "Mountain Standard / Pacific Daylight (GMT-7)",
  "-8": "Pacific Standard (GMT-8)",
  "-9": "Alaska (GMT-9)",
  "-10": "Hawaii (GMT-10)",
  "-11": "Samoa (GMT-11)"
};

// ---- 类 IANA 片段 → 偏移量（用于 label/value 中包含城市名的情况） ----
const IANA_OFFSETS = {
  new_york: -5,
  chicago: -6,
  denver: -7,
  phoenix: -7,
  los_angeles: -8,
  anchorage: -9,
  honolulu: -10,
  shanghai: 8
};

// 归一化字符串：转小写，空格/连字符归一为下划线，便于片段匹配
function normalize(str) {
  return String(str || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

// 在字符串中查找已知的 IANA 城市片段，返回 { hours, minutes: 0 }；未命中返回 null
function findIanaOffset(str) {
  if (!str) return null;
  const normalized = normalize(str);
  for (const key in IANA_OFFSETS) {
    if (Object.prototype.hasOwnProperty.call(IANA_OFFSETS, key)) {
      if (normalized.includes(key) || normalized.includes(key.replace(/_/g, ""))) {
        return { hours: IANA_OFFSETS[key], minutes: 0 };
      }
    }
  }
  return null;
}

// 从字符串中提取 GMT/UTC 偏移量。返回 { hours, minutes } 或 null。
// 规则（防误匹配）：
//  1) 仅匹配紧跟 GMT/UTC 的偏移（"GMT-5"、"UTC −5"、"GMT-05:30"、"GMT8"），
//     不再匹配任意位置的「符号+数字」（避免 "Region-5 (GMT-8)" 取错）
//  2) POSIX "Etc/GMT-8" 语义相反（表示 UTC+8）→ 反号
//  3) 整串裸偏移（"-5"、"+8"、"8"）视为偏移值
//  4) 中文「东N区 / 西N区」（阿拉伯数字或汉字数字）
function extractOffset(str) {
  if (!str) return null;
  const s = String(str).trim();
  // POSIX Etc/GMT±N：符号语义与直觉相反
  let match = s.match(/Etc\/GMT([+−-])(\d{1,2})/i);
  if (match) {
    const sign = match[1] === "+" ? -1 : 1;
    return { hours: sign * parseInt(match[2], 10), minutes: 0 };
  }
  // GMT/UTC 紧邻偏移（可带分钟）；无符号视为 "+"
  match = s.match(/(?:GMT|UTC)\s*([+−-]?)\s*(\d{1,2})(?::(\d{2}))?/i);
  if (match) {
    const sign = match[1] === "−" || match[1] === "-" ? -1 : 1;
    return { hours: sign * parseInt(match[2], 10), minutes: parseInt(match[3] || "0", 10) };
  }
  // 中文「东N区 / 西N区」
  match = s.match(/([东西])\s*(\d{1,2}|十[一二]?|[一二三四五六七八九])\s*区/);
  if (match) {
    const HANZI = { 一: 1, 二: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9, 十: 10, 十一: 11, 十二: 12 };
    const hours = HANZI[match[2]] !== undefined ? HANZI[match[2]] : parseInt(match[2], 10);
    return isNaN(hours) ? null : { hours: match[1] === "西" ? -hours : hours, minutes: 0 };
  }
  // 整串裸偏移（字典 value 常见形态："-5"、"+8"、"8"）
  match = s.match(/^([+−-]?)(\d{1,2})(?::(\d{2}))?$/);
  if (match) {
    const sign = match[1] === "−" || match[1] === "-" ? -1 : 1;
    return { hours: sign * parseInt(match[2], 10), minutes: parseInt(match[3] || "0", 10) };
  }
  return null;
}

// 将 label/value 转换为通俗时区名称；无法识别则原样返回 label（永不返回空）
export function friendlyTimeZone(label, value) {
  let offset = findIanaOffset(label);
  if (offset === null) {
    offset = findIanaOffset(value);
  }
  if (offset === null) {
    offset = extractOffset(value);
  }
  if (offset === null) {
    offset = extractOffset(label);
  }
  if (offset !== null) {
    // 含分钟的偏移（如 +5:30 印度）没有整点名称，标准化展示完整偏移
    if (offset.minutes) {
      const sign = offset.hours < 0 ? "-" : "+";
      return "GMT" + sign + Math.abs(offset.hours) + ":" + String(offset.minutes).padStart(2, "0");
    }
    const name = OFFSET_NAMES[String(offset.hours)];
    if (name) {
      return name;
    }
    // 无通俗名称的整点偏移也标准化展示，而不是保留后端原始文案
    return "GMT" + (offset.hours >= 0 ? "+" : "") + offset.hours;
  }
  return label;
}

// 批量转换字典列表（{id, label, value}[]），不修改原数组
export function mapTimeZoneList(list) {
  if (!Array.isArray(list)) {
    return list;
  }
  return list.map(item => ({
    ...item,
    label: friendlyTimeZone(item.label, item.value)
  }));
}

export default { friendlyTimeZone, mapTimeZoneList };
